import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import { TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';

import AdapterDateFns from '@mui/lab/AdapterDateFns';

import moment from 'moment';
import { useHistory, useParams } from 'react-router';
import Input from '../../../components/Input';
import { useAxios } from '../../../utils/hooks';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Form as FormModel } from '../../../models/form';
import { Record } from '../../../models/record';
import Loader from '../../../components/Loader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import { SelectProps } from '../../Records/List/dto';
import { User } from '../../../models/user';
import Select from '../../../components/Select';
import Signatures from '../../../components/Signatures';
import { SignatureForm } from '../../../models/signature-form';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    background: 'white',
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2),
  },
}));

const ForwardForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const api = useAxios();
  const [form, setForm] = useState<FormModel | null>(null);
  const [record, setRecord] = useState<Record | null>(null);
  const [signatures, setSignatures] = useState<SignatureForm[]>([]);
  const [users, setUsers] = useState<SelectProps[]>([]);
  const [date, setDate] = useState(new Date());
  const [authDate, setAuthDate] = useState<Date | null>(null);
  const { id, formId } = useParams<{ id: string; formId?: string }>();

  const formRef = useRef<FormHandles>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getRecordData() {
      try {
        const recordResponse = await api.current?.get(`/records/${id}`);

        if (recordResponse?.data) {
          setRecord(recordResponse.data);
        }

        const usersResponse = await api.current?.get('/users');

        if (usersResponse?.data) {
          setUsers(
            usersResponse.data.map((user: User) => ({
              value: user.id,
              label: `${user.firstName} ${user.lastName}`,
            })),
          );
        }

        if (formId) {
          const formResponse = await api.current?.get(`/forms/${formId}`);
          if (formResponse?.data) {
            setDate(
              formResponse?.data.answers.date
                ? moment(formResponse?.data.answers.date).toDate()
                : new Date(),
            );

            setAuthDate(
              formResponse?.data.answers.authDate
                ? moment(formResponse?.data.answers.authDate).toDate()
                : null,
            );
          }
          setForm(formResponse?.data);

          if (
            formResponse?.data.answers &&
            formResponse?.data.answers.signatures
          ) {
            setSignatures(formResponse?.data.answers.signatures);
          }
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar prontuário.',
        });
      } finally {
        setLoading(false);
      }
    }

    getRecordData();
  }, [api, id, formId]);

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        resetAlert();
        formRef.current?.setErrors({});

        // const schema = Yup.object().shape({
        //   content: Yup.string().required('Insira uma nota'),
        // });

        // await schema.validate(data, { abortEarly: false });
        data.professional =
          users.find((u) => u.value === data.professional_id)?.label || '';
        data.to_professional =
          users.find((u) => u.value === data.to_professional_id)?.label || '';
        data.date = moment(date).format('YYYY-MM-DD');
        if (authDate) data.authDate = moment(authDate).format('YYYY-MM-DD');

        const formRequest = {
          type: 'forwarding',
          title: 'Encaminhamento para Terapeuta',
          patient: record?.patient._id,
          record: record?._id,
          answers: { ...data, signatures },
        };

        if (formId) {
          await api.current?.patch(`/forms/${formId}`, formRequest);
        } else {
          await api.current?.post('/forms', formRequest);
        }

        history.push(`/prontuarios/${id}`);
      } catch (error: any) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else if (error.response && error.response.data) {
          switch (error.response.data.message) {
            case 'Form already sent':
              setAlert({
                isActive: true,
                type: 'warning',
                message: 'Formulário em análise, solicite a revisão.',
              });
              break;
            default:
              setAlert({
                isActive: true,
                type: 'error',
                message: 'Erro ao enviar encaminhamento.',
              });
              break;
          }
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao enviar encaminhamento.',
          });
        }
      }
    },
    [api, record, history, formId, date, authDate, id, users, signatures],
  );

  return (
    <>
      <Loader loading={loading} />

      {!loading && (
        <Form
          id="form"
          ref={formRef}
          initialData={form?.answers || { record: record?.number }}
          onSubmit={handleSubmit}
          className={classes.root}
        >
          <FormControl fullWidth className={classes.formControl}>
            <Typography
              variant="h5"
              component="h1"
              align="center"
              className={classes.formTitles}
            >
              FICHA PARA ENCAMINHAMENTO TERAPEUTA
            </Typography>

            <Divider className={classes.divider} />
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Select
                name="professional_id"
                defaultValue={
                  form?.answers
                    ? {
                        value: form?.answers.professional_id,
                        label: users.find(
                          (user) =>
                            user.value === form?.answers.professional_id,
                        )?.label,
                      }
                    : null
                }
                isMulti={false}
                placeholder="Terapeuta Responsável"
                options={users}
              />
            </Grid>

            <Grid item xs={6}>
              <Select
                name="to_professional_id"
                defaultValue={
                  form?.answers
                    ? {
                        value: form?.answers.to_professional_id,
                        label: users.find(
                          (user) =>
                            user.value === form?.answers.to_professional_id,
                        )?.label,
                      }
                    : null
                }
                isMulti={false}
                placeholder="Terapeuta Encaminhado"
                options={users}
              />
            </Grid>

            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="Data do encaminhamento"
                  inputFormat="dd/MM/yyyy"
                  value={date}
                  onChange={(d) => setDate(d as Date)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={8}>
              <Input id="area" label="Setor encaminhado" name="area" />
            </Grid>

            <Grid item xs={12}>
              <Input
                id="responsible_names"
                isMultiline
                rows={5}
                label="Nome dos terapeutas responsáveis (se houver mais de um no setor)"
                name="responsible_names"
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                id="motive"
                isMultiline
                rows={5}
                label="Motivo do encaminhamento"
                name="motive"
              />
            </Grid>

            <Grid item xs={4}>
              <Input
                id="professional_signature"
                label="Assinatura do terapeuta"
                name="professional_signature"
              />
            </Grid>

            <Grid item xs={4} />

            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="Data"
                  inputFormat="dd/MM/yyyy"
                  value={authDate}
                  onChange={(d) => setAuthDate(d as Date)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Assinaturas
                </FormLabel>
              </Grid>

              <Grid item xs={12} justify="center">
                <Signatures
                  formAnswers={form ? form.answers : {}}
                  onSignature={setSignatures}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.goBack()}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit" color="primary" variant="contained">
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Form>
      )}

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default ForwardForm;
